import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Phone, Check, Plus, Minus } from "lucide-react";
import Seo from "../components/Seo";
import pageSchemas from "../data/pageSchemas";

/**
 * Reusable SEO service landing page.
 * Data-driven — pass a `data` object with all copy blocks.
 *
 * Data shape:
 * {
 *   slug: "microneedling-san-diego",
 *   metaTitle, metaDescription, keywords,
 *   eyebrow: "Skin · San Diego",
 *   h1: "Microneedling in San Diego, CA",
 *   subtitle: "Collagen Induction Therapy for Smoother, Firmer Skin",
 *   heroImage: "/api/static/...",
 *   heroIntro: [ "paragraph 1", "paragraph 2" ],
 *   sections: [ { title, paragraphs: [], bullets?: [] } ],
 *   tiers: [ { name, price, blurb, ctaSlug (booking) } ],
 *   pairings: [ { title, description, link, linkLabel } ],
 *   faqs: [ { q, a } ],
 *   ctaTitle, ctaBlurb, ctaPhone: "858-905-4117"
 * }
 */
export default function ServiceLandingPage({ data }) {
    const [openFaq, setOpenFaq] = useState(0);

    // Prefer the SEO team's custom schemas when available (LocalBusiness + FAQPage).
    // Fall back to a generated FAQPage schema for pages the SEO team hasn't shipped schemas for.
    const customSchemas = pageSchemas[data.slug];
    const fallbackFaqSchema = data.faqs?.length ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: data.faqs.map(f => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a }
        }))
    } : null;
    const schemasToInject = customSchemas && customSchemas.length ? customSchemas : (fallbackFaqSchema ? [fallbackFaqSchema] : []);

    return (
        <div data-testid={`landing-${data.slug}`} className="bg-[#F2EDE2] min-h-screen">
            <Seo
                title={data.metaTitle}
                description={data.metaDescription}
                keywords={data.keywords}
                path={`/${data.slug}`}
                schema={schemasToInject}
                exactTitle
            />

            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 opacity-30">
                    {data.heroImage && (
                        <img src={data.heroImage} alt="" className="w-full h-full object-cover" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-b from-[#F2EDE2] via-[#F2EDE2]/70 to-[#F2EDE2]" />
                </div>
                <div className="relative max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16 pt-28 md:pt-36 pb-16 md:pb-24">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        {data.eyebrow && (
                            <div className="eyebrow eyebrow-gold">{data.eyebrow}</div>
                        )}
                        <h1 className="serif text-4xl md:text-5xl lg:text-[64px] leading-[1.05] mt-6 max-w-4xl">
                            {data.h1}
                        </h1>
                        {data.subtitle && (
                            <p className="serif italic text-2xl md:text-3xl text-[#A89070] mt-4 max-w-3xl">
                                {data.subtitle}
                            </p>
                        )}
                        {data.heroIntro && data.heroIntro.map((p, i) => (
                            <p key={i} className="mt-6 max-w-3xl text-[#544638] text-[17px] leading-[1.75]">
                                {p}
                            </p>
                        ))}
                        <div className="mt-10 flex flex-col sm:flex-row gap-3">
                            {data.tiers?.[0] && (
                                <Link
                                    to={`/book/${data.tiers[0].ctaSlug}`}
                                    data-testid="landing-hero-book"
                                    className="btn-gold inline-flex items-center justify-center gap-3"
                                >
                                    Book Consultation <ArrowRight size={14} />
                                </Link>
                            )}
                            <a
                                href={`tel:${(data.ctaPhone || "858-905-4117").replace(/\D/g, "")}`}
                                className="inline-flex items-center justify-center gap-2 px-6 py-4 text-[11px] tracking-[0.22em] uppercase text-[#262422] hover:text-[#A89070] transition"
                            >
                                <Phone size={14} /> {data.ctaPhone || "858-905-4117"}
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Tiers / Menu */}
            {data.tiers?.length > 0 && (
                <section className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-24" data-testid="landing-tiers">
                    <div className="eyebrow eyebrow-gold mb-4">{data.tiersEyebrow || "The Menu"}</div>
                    <h2 className="serif text-3xl md:text-4xl lg:text-5xl leading-[1.05] mb-12">
                        {data.tiersTitle || "Choose the tier that fits you."}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {data.tiers.map((t) => (
                            <div
                                key={t.name}
                                data-testid={`tier-${t.ctaSlug}`}
                                className="bg-white/50 border border-[#A89070]/25 rounded-sm p-7 flex flex-col hover:border-[#A89070]/60 hover:bg-white/70 transition"
                            >
                                <h3 className="serif text-2xl md:text-[26px] leading-tight">{t.name}</h3>
                                <div className="mt-3 flex items-baseline gap-2">
                                    <span className="text-[10px] tracking-[0.3em] uppercase text-[#8C7E6A]">
                                        {t.priceLabel || "From"}
                                    </span>
                                    <span className="serif text-3xl text-[#262422]">
                                        {/^\d/.test(String(t.price)) ? `$${t.price}` : t.price}
                                    </span>
                                </div>
                                <p className="mt-4 text-[15px] leading-relaxed text-[#544638] flex-1">
                                    {t.blurb}
                                </p>
                                <Link
                                    to={`/book/${t.ctaSlug}`}
                                    className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#262422] hover:text-[#A89070] transition"
                                >
                                    Book {t.name} <ArrowRight size={12} />
                                </Link>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Body sections */}
            <section className="max-w-[880px] mx-auto px-6 md:px-12 py-8 md:py-16 space-y-16">
                {data.sections?.map((s, i) => (
                    <div key={i} data-testid={`section-${i}`}>
                        {s.title && (
                            <h2 className="serif text-3xl md:text-4xl leading-[1.1] mb-6">{s.title}</h2>
                        )}
                        {s.paragraphs?.map((p, j) => (
                            typeof p === "string" ? (
                                <p key={j} className="text-[#544638] text-[17px] leading-[1.85] mb-4">{p}</p>
                            ) : (
                                <p key={j} className="text-[#544638] text-[17px] leading-[1.85] mb-4" dangerouslySetInnerHTML={{ __html: p.html }} />
                            )
                        ))}
                        {s.bullets?.length > 0 && (
                            <ul className="mt-4 space-y-3">
                                {s.bullets.map((b, k) => (
                                    <li key={k} className="flex items-start gap-3 text-[#262422]">
                                        <Check size={16} className="mt-1.5 text-[#A89070] shrink-0" />
                                        <span className="text-[16px] leading-relaxed">{b}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {s.numbered?.length > 0 && (
                            <ol className="mt-4 space-y-4 counter-reset-list">
                                {s.numbered.map((n, k) => (
                                    <li key={k} className="flex items-start gap-5">
                                        <span className="serif italic text-2xl text-[#A89070] shrink-0 w-8">
                                            {String(k + 1).padStart(2, "0")}
                                        </span>
                                        <span className="text-[16px] leading-relaxed pt-1"><strong className="text-[#262422]">{n.title}</strong> — {n.text}</span>
                                    </li>
                                ))}
                            </ol>
                        )}
                    </div>
                ))}
            </section>

            {/* Pairings / Related */}
            {data.pairings?.length > 0 && (
                <section className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-20 border-t border-[#A89070]/25">
                    <div className="eyebrow eyebrow-gold mb-4">Pair with</div>
                    <h2 className="serif text-3xl md:text-4xl leading-[1.05] mb-10">
                        Complete your treatment plan.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {data.pairings.map((p, i) => (
                            <Link
                                key={i}
                                to={p.link}
                                className="group bg-white/40 border border-[#A89070]/25 rounded-sm p-6 hover:bg-white/70 hover:border-[#A89070]/60 transition"
                            >
                                <h3 className="serif text-xl md:text-[22px]">{p.title}</h3>
                                <p className="mt-2 text-[14px] text-[#544638] leading-relaxed">{p.description}</p>
                                <div className="mt-4 text-[10px] tracking-[0.22em] uppercase text-[#A89070] group-hover:text-[#262422]">
                                    {p.linkLabel || "Explore"} →
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            {/* FAQ */}
            {data.faqs?.length > 0 && (
                <section className="bg-[#E6DEC9]/40 py-16 md:py-24" data-testid="landing-faqs">
                    <div className="max-w-[880px] mx-auto px-6 md:px-12">
                        <div className="eyebrow eyebrow-gold mb-4">FAQs</div>
                        <h2 className="serif text-3xl md:text-4xl lg:text-5xl leading-[1.05] mb-10">
                            Frequently asked.
                        </h2>
                        <div className="space-y-2">
                            {data.faqs.map((f, i) => {
                                const isOpen = openFaq === i;
                                return (
                                    <div key={i} className="bg-white/60 border border-[#A89070]/20 rounded-sm">
                                        <button
                                            onClick={() => setOpenFaq(isOpen ? -1 : i)}
                                            data-testid={`faq-toggle-${i}`}
                                            aria-expanded={isOpen}
                                            className="w-full text-left px-6 py-5 flex items-center justify-between gap-4 hover:bg-white/80 transition"
                                        >
                                            <span className="serif text-lg md:text-xl text-[#262422]">{f.q}</span>
                                            {isOpen ? <Minus size={18} className="text-[#A89070] shrink-0" /> : <Plus size={18} className="text-[#A89070] shrink-0" />}
                                        </button>
                                        {isOpen && (
                                            <div className="px-6 pb-5 pt-1 text-[#544638] text-[15px] leading-[1.75]">
                                                {f.a}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* Location + CTA */}
            <section className="bg-[#262422] text-[#F2EDE2] py-16 md:py-24">
                <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                    <div>
                        <div className="eyebrow text-[#A89070] mb-4">{data.ctaEyebrow || "Ready to book?"}</div>
                        <h2 className="serif text-3xl md:text-4xl lg:text-5xl leading-[1.05]">
                            {data.ctaTitle || "Let's talk."}
                        </h2>
                        <p className="mt-6 text-[#F2EDE2]/80 text-[16px] leading-[1.75] max-w-xl">
                            {data.ctaBlurb}
                        </p>
                    </div>
                    <div className="md:pl-8 border-t md:border-t-0 md:border-l border-[#A89070]/30 pt-8 md:pt-0 space-y-6">
                        <div className="flex items-start gap-3">
                            <MapPin size={18} className="text-[#A89070] mt-1 shrink-0" />
                            <div>
                                <div className="text-[10px] tracking-[0.28em] uppercase text-[#A89070] mb-1">Visit</div>
                                <div className="text-[15px] leading-relaxed">
                                    3727 Camino Del Rio S, Suite 210<br />
                                    San Diego, CA 92108
                                </div>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <Phone size={18} className="text-[#A89070] mt-1 shrink-0" />
                            <div>
                                <div className="text-[10px] tracking-[0.28em] uppercase text-[#A89070] mb-1">Call or text</div>
                                <a href={`tel:${(data.ctaPhone || "858-905-4117").replace(/\D/g, "")}`} className="serif text-2xl hover:text-[#A89070] transition">
                                    {data.ctaPhone || "858-905-4117"}
                                </a>
                            </div>
                        </div>
                        <Link
                            to={`/book/${data.tiers?.[0]?.ctaSlug || "signature-microneedling"}`}
                            data-testid="landing-footer-book"
                            className="mt-6 btn-gold inline-flex items-center gap-3"
                        >
                            Book Consultation <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
